import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import Swal from "sweetalert2";
import { usePermissions } from "../../utilis/PermissionsContext";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import CompanyFormsApprovePageHeader from "./CompanyFormsApprovePageHeader";
import CompanyFormsEmptyState from "./CompanyFormsEmptyState";
import CompanyFormsApproveInbox from "./CompanyFormsApproveInbox";
import { CompanyFormsApproveInboxSkeleton } from "./CompanyFormsSkeletons";
import { useCompanyFormCategories } from "./useCompanyFormCategories";
import {
  COMPANY_FORMS_MODULE_CONTENT,
  COMPANY_FORMS_MODULE_PAGE_SHELL,
  COMPANY_FORMS_ROOT,
  MODULE_PAGE_SHELL_STYLE,
  canUploadCompanyForms,
  canApproveCompanyForms,
} from "./companyFormConstants";

export default function CompanyFormsApprove() {
  const { categoryLabels } = useCompanyFormCategories();
  const { permissions, loading: permissionsLoading } = usePermissions();
  const permissionsReadyRef = useRef(false);

  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const canApprove = canApproveCompanyForms(permissions);
  const canUpload = canUploadCompanyForms(permissions);

  const uploadDocumentsBtnClass =
    "company-forms-btn inline-flex items-center rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] px-3.5 py-2 text-xs font-semibold text-[#007BFF] shadow-sm hover:bg-[#DBEAFE]/60";

  const uploadHeaderAction = canUpload ? (
    <Link to="/company-forms/manage" className={uploadDocumentsBtnClass}>
      Upload documents
    </Link>
  ) : null;

  const fetchPending = useCallback(async () => {
    if (!canApprove) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await apiUrl.get("/company-forms/pending");
      setPending(response?.data?.data || []);
      setSelectedIds(new Set());
    } catch {
      setPending([]);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [canApprove]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  useEffect(() => {
    if (!permissionsLoading) {
      permissionsReadyRef.current = true;
    }
  }, [permissionsLoading]);

  const toggleSelect = (item) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? new Set(pending.map((p) => p.id)) : new Set());
  };

  const handleApprove = async (item) => {
    if (!canApprove) return;
    const result = await Swal.fire({
      title: "Approve this document?",
      text: `"${item.form?.name || "This document"}" will be published for staff.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#007BFF",
      confirmButtonText: "Approve",
    });
    if (!result.isConfirmed) return;

    setBusyId(item.id);
    try {
      await apiUrl.post(`/company-forms/versions/${item.id}/approve`);
      Swal.fire({
        title: "Approved",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });
      fetchPending();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Approval failed", "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (item) => {
    if (!canApprove) return;
    const result = await Swal.fire({
      title: "Reject this upload?",
      text: "The uploader will see your note if you add one.",
      input: "textarea",
      inputPlaceholder: "Reason (optional)",
      inputAttributes: { "aria-label": "Rejection reason" },
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      confirmButtonText: "Reject",
    });
    if (!result.isConfirmed) return;

    setBusyId(item.id);
    try {
      await apiUrl.post(`/company-forms/versions/${item.id}/reject`, {
        rejection_note: result.value || "",
      });
      Swal.fire({
        title: "Rejected",
        icon: "info",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });
      fetchPending();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Reject failed", "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleBulkApprove = async () => {
    if (!canApprove) return;
    const items = pending.filter((p) => selectedIds.has(p.id));
    if (!items.length) return;

    const result = await Swal.fire({
      title: `Approve ${items.length} document${items.length > 1 ? "s" : ""}?`,
      text: "Selected uploads will be published for staff.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#007BFF",
      confirmButtonText: "Approve all",
    });
    if (!result.isConfirmed) return;

    setBulkBusy(true);
    try {
      for (const item of items) {
        await apiUrl.post(`/company-forms/versions/${item.id}/approve`);
      }
      Swal.fire({
        title: "Approved",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2200,
      });
      fetchPending();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Bulk approval failed", "error");
      fetchPending();
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkReject = async () => {
    if (!canApprove) return;
    const items = pending.filter((p) => selectedIds.has(p.id));
    if (!items.length) return;

    const result = await Swal.fire({
      title: `Reject ${items.length} upload${items.length > 1 ? "s" : ""}?`,
      text: "The same note will apply to each selected upload if you add one.",
      input: "textarea",
      inputPlaceholder: "Reason (optional)",
      inputAttributes: { "aria-label": "Rejection reason" },
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      confirmButtonText: "Reject all",
    });
    if (!result.isConfirmed) return;

    setBulkBusy(true);
    try {
      const note = result.value || "";
      for (const item of items) {
        await apiUrl.post(`/company-forms/versions/${item.id}/reject`, {
          rejection_note: note,
        });
      }
      Swal.fire({
        title: "Rejected",
        icon: "info",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2200,
      });
      fetchPending();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Bulk reject failed", "error");
      fetchPending();
    } finally {
      setBulkBusy(false);
    }
  };

  if (permissionsLoading && !permissionsReadyRef.current) {
    return (
      <div className={COMPANY_FORMS_ROOT}>
        <CompanyFormsApprovePageHeader actions={uploadHeaderAction} />
        <div className={COMPANY_FORMS_MODULE_PAGE_SHELL} style={MODULE_PAGE_SHELL_STYLE}>
          <div className={COMPANY_FORMS_MODULE_CONTENT}>
            <CompanyFormsApproveInboxSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (!canApprove) {
    return (
      <div className={COMPANY_FORMS_ROOT}>
        <CompanyFormsApprovePageHeader actions={uploadHeaderAction} />
        <div className={COMPANY_FORMS_MODULE_PAGE_SHELL} style={MODULE_PAGE_SHELL_STYLE}>
          <div className={`${COMPANY_FORMS_MODULE_CONTENT} py-6`}>
            <CompanyFormsEmptyState
              icon={FileText}
              title="Access restricted"
              description="Approve permission is required to review uploads."
            >
              <Link
                to="/company-forms"
                className="company-forms-btn inline-flex rounded-lg bg-[#007BFF] px-4 py-2 text-xs font-semibold text-white no-underline hover:bg-[#0069d9]"
              >
                View documents
              </Link>
            </CompanyFormsEmptyState>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={COMPANY_FORMS_ROOT}>
      <CompanyFormsApprovePageHeader actions={uploadHeaderAction} />

      <div className={COMPANY_FORMS_MODULE_PAGE_SHELL} style={MODULE_PAGE_SHELL_STYLE}>
        <div className={COMPANY_FORMS_MODULE_CONTENT}>
          {loading && pending.length === 0 ? (
            <CompanyFormsApproveInboxSkeleton />
          ) : (
            <CompanyFormsApproveInbox
              items={pending}
              busyId={busyId}
              bulkBusy={bulkBusy}
              categoryLabels={categoryLabels}
              onApprove={handleApprove}
              onReject={handleReject}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onSelectAll={handleSelectAll}
              onClearSelection={() => setSelectedIds(new Set())}
              onBulkApprove={handleBulkApprove}
              onBulkReject={handleBulkReject}
            />
          )}
        </div>
      </div>
    </div>
  );
}
